"use client";

import { useContext, useEffect, useState, Fragment } from "react";
import { useRouter } from "next/navigation";
import { useCookies } from "react-cookie";

import { Button } from "@/components/Button";
import { Hamburger } from "@/components/Hamburger";
import { Wrapper } from "@/components/Wrapper";
import { Logo } from "@/components/Logo";

import { getSiteInfo } from "@/sites";

import { isClient } from "@/utils/browser";
import { paths } from "@/utils/paths";

import { AMPLITUDE_EVENT_NAME } from "@/internals/constants/events";
import { AUTHORIZATION } from "@/internals/constants/cookies";

import { LayoutContext } from "@/contexts/LayoutContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";

import { useLogin } from "@/hooks/auth/useLogin";
import { useDomainConfig } from "@/hooks/domain/useDomainConfig";

import { HeaderProps } from "./types";
import { SiteName } from "@/sites/types";

import "./styles.scss";

export const Header = ({ noNav }: HeaderProps) => {
  const layoutContext = useContext(LayoutContext);
  const { setSource } = useContext(AmplitudeContext);

  const { isUserLoggedIn } = useLogin();
  const { logoLinkEnabled } = useDomainConfig();
  const [cookies] = useCookies();
  const router = useRouter();

  const [isRegisterPage, setIsRegisterPage] = useState(false);
  const [isUserLogged, setIsUserLogged] = useState(false);

  const { siteBlackLogo28 } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};
  const location = isClient() ? window.location : undefined;

  const navigateTo = (path: string) => {
    router.push(path);
    layoutContext?.setIsMenuOpened(false);
  };

  useEffect(() => {
    setIsRegisterPage(location?.pathname.includes("register"));
    const currentLoginStatus = Boolean(isUserLoggedIn());
    setIsUserLogged(currentLoginStatus);
  }, [location?.pathname]);

  useEffect(() => {
    const hasAuthCookie = Boolean(cookies?.[AUTHORIZATION]);
    setIsUserLogged(hasAuthCookie);
  }, [cookies]);

  const { siteName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);

  const overFlowMenuClass = () => {
    const bodyElement = document.querySelector("body");
    const htmlElement = document.querySelector("html");
    bodyElement.classList.remove("overflowHidden");
    htmlElement.classList.remove("overflowHidden");
  };

  const handleClickMenu = (event, navPath) => {
    overFlowMenuClass();

    if (event) {
      amplitudeTrackEvent(AMPLITUDE_EVENT_NAME[event], {
        source: "navbar",
      });
    }
    navigateTo(navPath);
  };

  return (
    <header
      data-testid="header"
      className="header">
      <Wrapper customClass="header__wrapper">
        <Logo
          href={logoLinkEnabled && "/"}
          src={siteBlackLogo28}
          siteName={siteName}
          dataQa="header-logo-link"
          dataTestId="header-logo"
          className="header__logo"
          setSource={setSource}
        />
        {!noNav && (
          <div className="nav__wrapper">
            <Fragment>
              {!isRegisterPage && (
                <Hamburger>
                  {isUserLogged ? (
                    <Button
                      dataQA="header-dashboard-button"
                      tertiary
                      className="account"
                      label="Go to my account"
                      onClick={() => handleClickMenu(undefined, paths.myQrCodes())}
                      rounded={siteName === SiteName.QRNOW}
                    />
                  ) : (
                    <Fragment>
                      <Button
                        ghost
                        buttonId="login"
                        dataQA="header-login-button"
                        label="Login"
                        onClick={() => handleClickMenu("view_landing_login", paths.login())}
                      />
                      <Button
                        tertiary
                        className="signup"
                        label="Sign up"
                        dataQA="header-sign-up-button"
                        onClick={() => handleClickMenu("view_landing_signup", paths.signup())}
                        rounded={siteName === SiteName.QRNOW}
                      />
                    </Fragment>
                  )}
                </Hamburger>
              )}
            </Fragment>
          </div>
        )}
      </Wrapper>
    </header>
  );
};
